import { useState } from "react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#/components/ui/card";
import { Skeleton } from "#/components/ui/skeleton";
import { getMyPlayerProfile } from "#/server/getMyPlayerProfile";

import {
  StatsBreakdownAccordion,
  StatsPrimaryRows,
  type StatsBreakdownSection,
} from "./-profile.breakdownAccordion";
import { PROFILE_UNITS_KILLED_KINDS } from "./-profile.breakdownRows";

export type PlayerProfile = Awaited<ReturnType<typeof getMyPlayerProfile>>;

function sumRecord(record: Partial<Record<string, number>>): number {
  let total = 0;
  for (const value of Object.values(record)) {
    if (typeof value === "number") total += value;
  }
  return total;
}

function sumRecordKeys(record: Partial<Record<string, number>>, keys: readonly string[]): number {
  let total = 0;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number") total += value;
  }
  return total;
}

function StatsCardHeader() {
  return (
    <CardHeader>
      <CardTitle>Stats</CardTitle>
      <CardDescription>Totals from your completed games.</CardDescription>
    </CardHeader>
  );
}

export function StatsCardSkeleton() {
  return (
    <Card>
      <StatsCardHeader />
      <CardContent>
        <StatsListSkeleton />
      </CardContent>
    </Card>
  );
}

export function StatsCard({ profile }: { profile: PlayerProfile }) {
  return (
    <Card>
      <StatsCardHeader />
      <CardContent>
        <StatsList player={profile} />
      </CardContent>
    </Card>
  );
}

function profileSimpleStatItems(player: PlayerProfile): Array<{ label: string; value: number }> {
  return [
    { label: "Games completed", value: player.gamesCompleted },
    { label: "Standard daily streak", value: player.standardDailyStreak },
    {
      label: "Longest standard streak",
      value: Math.max(player.standardDailyStreak, player.standardLongestDailyStreak),
    },
    { label: "Premium daily streak", value: player.premiumDailyStreak },
    {
      label: "Longest premium streak",
      value: Math.max(player.premiumDailyStreak, player.premiumLongestDailyStreak),
    },
    { label: "Income collected", value: player.incomeCollected },
  ];
}

function StatsList({ player }: { player: PlayerProfile }) {
  const [openAccordionValues, setOpenAccordionValues] = useState<string[]>([]);
  const simpleItems = profileSimpleStatItems(player);

  const breakdownSections: StatsBreakdownSection[] = [
    {
      value: "units-built",
      label: "Units built",
      total: sumRecord(player.unitsBuilt),
      breakdown: "units",
      record: player.unitsBuilt,
    },
    {
      value: "units-killed",
      label: "Units killed",
      total: sumRecordKeys(player.unitsKilled, PROFILE_UNITS_KILLED_KINDS),
      breakdown: "units",
      record: player.unitsKilled,
    },
    {
      value: "buildings-captured",
      label: "Buildings captured",
      total: sumRecord(player.buildingsCaptured),
      breakdown: "buildings",
      record: player.buildingsCaptured,
    },
  ];

  return (
    <div className="flex flex-col gap-0">
      <StatsPrimaryRows items={simpleItems} />
      <StatsBreakdownAccordion
        openValues={openAccordionValues}
        onOpenChange={setOpenAccordionValues}
        simpleRowCount={simpleItems.length}
        sections={breakdownSections}
      />
    </div>
  );
}

function StatsListSkeleton() {
  return (
    <div className="flex flex-col gap-0">
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="py-2 px-4">
          <Skeleton className="h-5 w-full" />
        </div>
      ))}
    </div>
  );
}
